"use client"

import type { TutorialSlide } from "@/data/tutorial-slides"

interface TutorialSlideProps {
  slide: TutorialSlide
}

// Each annotation is capped to just under half the width so that the two
// annotations sharing a row can never run into each other - the old layout
// pinned raw text to the corners and long labels collided in the middle.
const POSITION_CLASSES: Record<TutorialSlide["annotations"][number]["position"], string> = {
  "top-left": "top-1.5 left-1.5 text-left",
  "top-right": "top-1.5 right-1.5 text-right",
  "bottom-left": "bottom-1.5 left-1.5 text-left",
  "bottom-right": "bottom-1.5 right-1.5 text-right",
}

export const TutorialSlideComponent = ({ slide }: TutorialSlideProps) => {
  return (
    /* Tall screens stack: title, description, annotated screenshot.
       Short screens (landscape phones) go side-by-side instead - the screenshot
       on the left, the text and a legend on the right - because stacking made
       the slide taller than the whole viewport. */
    <div
      className="flex flex-col items-center gap-2 px-4 md:gap-3 md:px-8
                 short:grid short:grid-cols-[auto_1fr] short:items-start short:gap-x-4 short:gap-y-1 short:px-3"
    >
      {/* Title */}
      <h2
        className="text-xl font-bold italic text-center md:text-3xl
                   short:col-start-2 short:row-start-1 short:text-left short:text-lg"
        style={{ color: "#2d1810" }}
      >
        {slide.title}
      </h2>

      {/* Description */}
      <p
        className="max-w-2xl text-center text-xs italic leading-snug md:text-base md:leading-relaxed
                   short:col-start-2 short:row-start-2 short:text-left short:text-[11px]"
        style={{ color: "#2d1810" }}
      >
        {slide.description}
      </p>

      {/* Screenshot with annotations.
          Sized by height rather than width so the slide always fits the
          viewport. Aspect matches the in-game view it was captured from, so
          nothing is stretched. */}
      <div
        className="flex w-full max-w-3xl justify-center
                   short:col-start-1 short:row-span-3 short:row-start-1 short:w-auto"
        style={{ height: "min(46dvh, 26rem)" }}
      >
        <div className="relative h-full max-w-full aspect-[6/5] overflow-hidden rounded border-2 border-amber-900/30 bg-slate-800">
          <img
            src={slide.image}
            alt={slide.title}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ imageRendering: "pixelated" }}
            draggable={false}
          />

          {/* Annotations overlaid on the screenshot. On short viewports the
              image is too small for four wrapped labels to sit in the corners
              without colliding, so the legend below takes over instead. */}
          {slide.annotations.map((annotation, index) => (
            <div
              key={index}
              className={`absolute max-w-[46%] rounded bg-black/70 px-1.5 py-1 text-[10px] font-semibold italic leading-tight md:text-sm short:hidden ${
                POSITION_CLASSES[annotation.position]
              }`}
              style={{ color: "#ffe0e0", textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}
            >
              {annotation.text}
            </div>
          ))}
        </div>
      </div>

      {/* Legend, shown instead of the overlay on short viewports */}
      <ul
        className="hidden text-[11px] italic leading-tight
                   short:col-start-2 short:row-start-3 short:block short:space-y-0.5"
        style={{ color: "#5b2d1b" }}
      >
        {slide.annotations.map((annotation, index) => (
          <li key={index} className="flex gap-1">
            <span aria-hidden="true">&bull;</span>
            <span>{annotation.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
